/**
 * Collapses the wizard's two patient search lists into one row per person.
 *
 * THE PROBLEM THIS SOLVES
 * -----------------------
 * The picker runs two unrelated queries: `patient_summary_list` over
 * `patients.full_name`, and an order search over `order_lines.patient_name`.
 * They were rendered as two sibling lists with nothing comparing them, so the
 * same human could appear twice, once as a "Patient record" that fills only
 * name / age / sex, and once as an order row that copies everything. Two rows,
 * one person, different behaviour: a trap for the podiatrist.
 *
 * WHY IT IS NOT A NAME MATCH
 * --------------------------
 * A name is not an identity. Two people in one clinic can share one, and 10
 * such groups exist in production today. So a record-less order is NEVER
 * merged into a patient record by name. It is folded in only when a real
 * foreign key says so:
 *
 *   1. `orders.patient_id`, or
 *   2. `order_lines.patient_id`  <- the key both RPCs ignored. ensure_line_patient
 *      sets it without ever setting the header's, which is why 15 QR-minted
 *      patients appeared twice and reported "0 past orders" while being linked.
 *
 * Orders that resolve to neither are grouped with EACH OTHER by
 * (clinic_id, normalised name) and labelled "no record on file". That grouping
 * is a bucket, not an assertion of identity, and it is the honest thing to show
 * for the 524 orders that genuinely have no patient record.
 *
 * Deliberately pure and free of React and supabase imports: PatientPicker
 * cannot be unit-tested (it eagerly imports @/lib/supabase), so anything worth
 * testing has to live in a sibling like this one. Same reason as
 * patientModeReset.ts.
 */

export interface PersonRowRecord {
  kind: 'record';
  /** Stable list key. */
  id: string;
  patientId: string;
  displayName: string;
  clinicId: string | null;
  clinicName: string | null;
  patientCode: string | null;
  patientIdShort: string | null;
  /** Past orders, from the RPC's own count (now over the key union). */
  orderCount: number;
  lastOrderAt: string | null;
}

export interface PersonRowCluster {
  kind: 'name_only';
  id: string;
  displayName: string;
  clinicId: string | null;
  clinicName: string | null;
  /** The record-less orders grouped under this name, newest first. */
  orderCount: number;
  lastOrderAt: string | null;
  /** Indices into the input order list, so the caller can render/act on them. */
  orderIndexes: number[];
}

export type PersonRow = PersonRowRecord | PersonRowCluster;

/** Minimal shape of a `patient_summary_list` row that this module reads. */
export interface MergeablePatientRow {
  id: string;
  full_name: string;
  clinic_id: string | null;
  patient_code: string | null;
  patient_id_short: string | null;
  total_orders: number;
  last_order_date: string | null;
}

/** Minimal shape of an order-search row that this module reads. */
export interface MergeableOrderRow {
  order_id: string;
  created_at: string;
  patient_name: string;
  order_patient_id: string | null;
  line_patient_id: string | null;
  clinic: { id: string; name: string } | null;
}

/** Case- and whitespace-insensitive name key. Never crosses a clinic. */
export function nameKey(clinicId: string | null, name: string): string {
  return `${clinicId ?? 'none'}::${name.trim().toLowerCase().replace(/\s+/g, ' ')}`;
}

function newer(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/**
 * One row per person.
 *
 * Records come first and keep their RPC-reported order count. Clusters follow,
 * ordered by their most recent order. An order is dropped from the cluster
 * pass entirely if either patient key resolves, whether or not the matching
 * record is in `patients` (an admin search can return an order whose patient
 * record is out of the current page).
 */
export function mergePatientSearchResults(
  patients: readonly MergeablePatientRow[],
  orders: readonly MergeableOrderRow[],
): PersonRow[] {
  const recordRows: PersonRowRecord[] = patients.map((p) => ({
    kind: 'record',
    id: `record:${p.id}`,
    patientId: p.id,
    displayName: p.full_name,
    clinicId: p.clinic_id,
    clinicName: null,
    patientCode: p.patient_code,
    patientIdShort: p.patient_id_short,
    orderCount: p.total_orders,
    lastOrderAt: p.last_order_date,
  }));

  // An order is folded away ONLY when the record it belongs to is actually on
  // screen to fold it into.
  //
  // The obvious version of this ("carries a patient key, therefore drop it")
  // silently deletes orders. The two queries match on DIFFERENT columns:
  // records match `patients.full_name`, orders match
  // `order_lines.patient_name`, and 43 live orders have a line name that
  // differs from their linked record's name (33 of them not yet delivered).
  // For those, typing the name printed on the prescription returns the order
  // but NOT the record, so dropping the order leaves an empty result box and
  // no route to the order at all. On main every order row rendered.
  //
  // Folding stays keyed on the FOREIGN KEY, never on a name, so the QR-minted
  // duplicates still dissolve: those 17 records do match by name, so they are
  // present and their orders fold correctly.
  const recordIds = new Set(patients.map((p) => p.id));
  const clusters = new Map<string, PersonRowCluster>();
  orders.forEach((o, index) => {
    const linkedTo = o.order_patient_id ?? o.line_patient_id;
    if (linkedTo && recordIds.has(linkedTo)) return;
    const clinicId = o.clinic?.id ?? null;
    const key = nameKey(clinicId, o.patient_name);
    const existing = clusters.get(key);
    if (existing) {
      existing.orderCount += 1;
      existing.lastOrderAt = newer(existing.lastOrderAt, o.created_at);
      existing.orderIndexes.push(index);
      return;
    }
    clusters.set(key, {
      kind: 'name_only',
      id: `cluster:${key}`,
      displayName: o.patient_name.trim(),
      clinicId,
      clinicName: o.clinic?.name ?? null,
      orderCount: 1,
      lastOrderAt: o.created_at,
      orderIndexes: [index],
    });
  });

  const clusterRows = [...clusters.values()].sort((a, b) =>
    (b.lastOrderAt ?? '').localeCompare(a.lastOrderAt ?? ''),
  );

  // Records rank above clusters: a real record is the canonical identity, and
  // picking it links the order to it.
  return [...recordRows, ...clusterRows];
}
